// composables/useLanguage.js
import { ref, computed, getCurrentInstance } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'

const currentLocale = ref(localStorage.getItem('locale') || 'en-US')
const localeOptions = ref([])
const localeParams = ref(null)
const langId = ref(1)

export function useLanguage() {
  const { locale, setLocaleMessage, t } = useI18n({ useScope: 'global' }) 
  const { proxy } = getCurrentInstance()
  const $q = useQuasar()
  const $store = proxy.$store

  /**
   * Load available locale options from server
   */
  const loadLocaleOptions = async () => {
    if (localeOptions.value.length > 0) {
      return localeOptions.value
    }

    try {
      // Koristi proxy.get kao u useAppInit!
      const data = await proxy.get('CommonAnon/GetLocaleOptions', null, true)
      localeOptions.value = data
      
      localeParams.value = localeOptions.value.find(
        item => item.value === currentLocale.value
      )
      
      console.log('Loaded locale options:', localeOptions.value)
      return localeOptions.value
    } catch (error) {
      console.error('Error loading locale options:', error)
      // Fallback
      localeOptions.value = [
        { id: 1, value: 'en-US', label: 'English (US)', short_tag: 'EN' }
      ]
      return localeOptions.value
    }
  }

  /**
   * Change application locale
   * @param {string|object} localeInput - Locale value string or locale object
   */
  const changeLocale = async (localeInput) => {
    if (!localeOptions.value || localeOptions.value.length === 0) {
      console.warn('Locale options not loaded')
      return
    }

    try {
      let lang
      
      // Handle both string and object input
      if (typeof localeInput === 'string') {
        lang = localeOptions.value.find(item => item.value === localeInput)
      } else {
        lang = localeInput
      }
      
      if (!lang) {
        throw new Error(`Locale not found: ${localeInput}`)
      }

      // Update refs
      localeParams.value = lang
      currentLocale.value = lang.value
      langId.value = lang.id
      
      // Update store (for axios headers)
      $store.locale = lang.value
      $store.localeParams = lang
      $store.langId = lang.id
      
      // Update localStorage
      localStorage.setItem('locale', lang.value)
      

      // Load i18n translations - koristi proxy.get!
      const translations = await proxy.get('CommonAnon/GetI18N', null, true)


    
    //    $i18n.global.setLocaleMessage(lang.value, translations);
    // $i18n.global.locale = lang.value;
      setLocaleMessage(lang.value, translations);
      locale.value = lang.value;
       

      // Load Quasar language pack
      try {
        const langQ = await import(`../../localization/quasar/${lang.value}.mjs`)
        $q.lang.set(langQ.default)
      } catch (error) {
        console.warn(`Quasar lang not found for ${lang.value}:`, error)
      }

      console.log('Language changed to:', lang.label)
      return true
    } catch (error) {
      console.error('Error changing locale:', error)
      throw error
    }
  }

  /**
   * Revert to English locale
   */
  const revertToEnglish = () => {
    currentLocale.value = 'en-US'
    langId.value = 1
    $store.locale = 'en-US'
    $store.langId = 1
    localStorage.setItem('locale', 'en-US')
    // $i18n.global.locale = 'en-US'
    locale.value = 'en-US'
    
    try {
      $q.lang.set($q.lang['en-us'])
    } catch (error) {
      console.error('Error setting Quasar lang:', error)
    }
  }

  /**
   * Initialize language system
   */
  // const initializeLanguage = async () => {
  //   await loadLocaleOptions()
    
  //   const savedLocale = localStorage.getItem('locale') || 'en-US'

  //   // Pronađi locale objekt i pozovi changeLocale
  //   const localeObj = localeOptions.value.find(item => item.value === savedLocale)

  //   if (localeObj) {
  //       await changeLocale(localeObj)
  //   } else if (localeOptions.value.length > 0) {
  //       // Fallback na prvi dostupan jezik
  //       await changeLocale(localeOptions.value[0])
  //   }
  //   // currentLocale.value = savedLocale
  //   // // $i18n.global.locale = savedLocale;
  //   // locale.value = savedLocale;


  //   // localeParams.value = localeOptions.value.find(
  //   //   item => item.value === currentLocale.value
  //   // )
    
  //   // console.log('Language initialized:', localeParams.value)
  // }
  const initializeLanguage = async () => {
    await loadLocaleOptions()
    
    const savedLocale = localStorage.getItem('locale')
    
    let targetLocale = null
    
    if (savedLocale) {
      // Korisnik je već odabrao jezik
      targetLocale = localeOptions.value.find(item => item.value === savedLocale)
    }
    
    if (!targetLocale) {
      // Detektiraj iz preglednika
      const browserLocales = navigator.languages || [navigator.language]
      
      for (const browserLocale of browserLocales) {
        // Probaj exact match: "hr-HR" === "hr-HR"
        targetLocale = localeOptions.value.find(
          item => item.value.toLowerCase() === browserLocale.toLowerCase()
        )
        if (targetLocale) break
        
        // Probaj language prefix: "hr" iz "hr-HR"
        const langPrefix = browserLocale.split('-')[0].toLowerCase()
        targetLocale = localeOptions.value.find(
          item => item.value.toLowerCase().startsWith(langPrefix)
        )
        if (targetLocale) break
      }
    }
    
    // Fallback na engleski
    if (!targetLocale) {
      targetLocale = localeOptions.value.find(item => item.value === 'en-US') 
        || localeOptions.value[0]
    }
    
    await changeLocale(targetLocale)
  }

  return {
    currentLocale: computed(() => currentLocale.value),
    currentLocaleShort: computed(() => currentLocale.value.split('-')[0]),
    localeOptions: computed(() => localeOptions.value),
    localeParams: computed(() => localeParams.value),
    langId: computed(() => langId.value),
    loadLocaleOptions,
    changeLocale,
    revertToEnglish,
    initializeLanguage
  }
}